import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function statusPill(ok, labelOk = 'OK', labelBad = 'Needs attention') {
  const bg = ok ? '#16a34a' : '#ef4444';
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: '#fff',
      background: bg,
      whiteSpace: 'nowrap',
    }}>
      {ok ? labelOk : labelBad}
    </span>
  );
}

function RevealRow({ label, masked, raw, allowReveal = true }) {
  const [revealed, setRevealed] = useState(false);
  const value = revealed ? (raw || masked || '—') : (masked || '—');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(raw || masked || ''));
    } catch (e) {
      // ignore
    }
  };

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #1f2a44' }}>
      <div>
        <div style={{ color: '#94a3b8', fontSize: 12 }}>{label}</div>
        <div style={{ color: '#e2e8f0', fontSize: 14, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{value}</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {allowReveal && raw && (
          <button
            onClick={() => setRevealed(v => !v)}
            style={styles.btn}
            title={revealed ? 'Hide' : 'Reveal'}
          >
            {revealed ? 'Hide' : 'Reveal'}
          </button>
        )}
        <button onClick={copy} style={styles.btn} title="Copy">Copy</button>
      </div>
    </div>
  );
}

function AdvancedComms() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getAdvancedComms();
      setData(res);
      const first = res?.sites?.[0]?.site_id;
      setSelectedSiteId(prev => prev || first || null);
    } catch (e) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selected = useMemo(() => {
    if (!data?.sites?.length) return null;
    return data.sites.find(s => s.site_id === selectedSiteId) || data.sites[0];
  }, [data, selectedSiteId]);

  const diagnosticsBlock = useMemo(() => {
    if (!data || !selected) return '';
    const sms = selected?.sms || {};
    const inbound = (sms?.inbound_mappings || []).map(m => m.to_number_e164 || m.to_number_masked).filter(Boolean);
    const out = sms?.outbound_identity || {};

    return [
      `Dealer: ${data.dealer_id}`,
      `Site: ${selected.site_name} (${selected.site_id})`,
      `Region: ${selected.region}`,
      `SMS Ready: ${sms.ready ? 'YES' : 'NO'}`,
      `SMS Inbound Mappings: ${inbound.length ? inbound.join(', ') : 'NONE'}`,
      `SMS Outbound MSID: ${out.messaging_service_sid || 'NONE'}`,
      `SMS Outbound From: ${out.from_number_e164 || 'NONE'}`,
      `Last inbound: ${sms.last_inbound_at || 'NONE'}`,
      `Last outbound: ${sms.last_outbound_at || 'NONE'}`,
      `Email From: ${data?.email?.from_email || 'NONE'}`,
      `Mailgun Domain: ${data?.email?.mailgun_domain || 'NONE'}`,
      `Reply Domain: ${data?.email?.reply_domain || 'NONE'}`,
    ].join('\n');
  }, [data, selected]);

  const copyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(diagnosticsBlock);
    } catch {
      // ignore
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading comms diagnostics…</div>;
  }

  if (error) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>📡 Advanced Comms</div>
        <div style={{ color: '#fecaca', marginTop: 10 }}>{error}</div>
        <button onClick={load} style={{ ...styles.btn, marginTop: 12 }}>Retry</button>
      </div>
    );
  }

  const sites = data?.sites || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={styles.card}>
        <div style={styles.title}>📡 Advanced → Comms</div>
        <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>
          Dealer-wide rollup + per-site drilldown to verify inbound/outbound setup.
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ fontWeight: 800, color: '#e2e8f0' }}>Rollup</div>
          <button onClick={load} style={styles.btn}>Refresh</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          <div style={styles.kpi}>
            <div style={styles.kpiLabel}>SMS (all sites)</div>
            <div>{statusPill(!!data?.rollup?.sms_ready, 'Green', 'Red')}</div>
          </div>
          <div style={styles.kpi}>
            <div style={styles.kpiLabel}>Unmapped inbound alerts (dealer)</div>
            <div style={{ color: '#e2e8f0', fontWeight: 700 }}>
              24h: {data?.rollup?.alerts?.twilio_inbound_unmapped_to_number?.count_24h ?? '—'} | 7d: {data?.rollup?.alerts?.twilio_inbound_unmapped_to_number?.count_7d ?? '—'}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14, overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>SMS Status</th>
                <th style={styles.th}>Last inbound</th>
                <th style={styles.th}>Last outbound</th>
              </tr>
            </thead>
            <tbody>
              {sites.map(s => (
                <tr
                  key={s.site_id}
                  onClick={() => setSelectedSiteId(s.site_id)}
                  style={{
                    ...styles.tr,
                    background: selected?.site_id === s.site_id ? '#111c33' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <td style={styles.td}>
                    <div style={{ color: '#e2e8f0', fontWeight: 700 }}>{s.site_name}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{s.region} • {s.site_slug}</div>
                  </td>
                  <td style={styles.td}>{statusPill(!!s?.sms?.ready, 'Green', 'Red')}</td>
                  <td style={styles.td}><span style={{ color: '#e2e8f0' }}>{fmtTs(s?.sms?.last_inbound_at)}</span></td>
                  <td style={styles.td}><span style={{ color: '#e2e8f0' }}>{fmtTs(s?.sms?.last_outbound_at)}</span></td>
                </tr>
              ))}
              {!sites.length && (
                <tr><td style={styles.td} colSpan={4}>No sites found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.grid2}>
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 800, color: '#e2e8f0' }}>Drilldown</div>
            <button onClick={copyDiagnostics} style={styles.btn}>Copy diagnostics</button>
          </div>

          {!selected ? (
            <div style={{ color: '#94a3b8', marginTop: 10 }}>Select a site to view details.</div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <div style={{ color: '#e2e8f0', fontWeight: 800, fontSize: 16 }}>{selected.site_name}</div>
              <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{selected.site_id}</div>

              <div style={{ marginTop: 14, color: '#93c5fd', fontWeight: 800 }}>SMS</div>
              <RevealRow
                label="Outbound Messaging Service SID"
                masked={selected?.sms?.outbound_identity?.messaging_service_sid_masked}
                raw={selected?.sms?.outbound_identity?.messaging_service_sid}
              />
              <RevealRow
                label="Outbound From number"
                masked={selected?.sms?.outbound_identity?.from_number_masked}
                raw={selected?.sms?.outbound_identity?.from_number_e164}
              />
              <div style={{ padding: '8px 0', borderBottom: '1px solid #1f2a44' }}>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Outbound identity source</div>
                <div style={{ color: '#e2e8f0', fontSize: 14 }}>{selected?.sms?.outbound_identity?.source || '—'}</div>
              </div>

              <div style={{ padding: '8px 0', borderBottom: '1px solid #1f2a44' }}>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>Inbound mappings</div>
                <div style={{ color: '#e2e8f0', fontSize: 14 }}>
                  {(selected?.sms?.inbound_mappings || []).length ? '' : 'NONE'}
                </div>
                {(selected?.sms?.inbound_mappings || []).map(m => (
                  <div key={m.id} style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', color: '#e2e8f0' }}>
                      {m.to_number_masked}
                      {m.label ? <span style={{ color: '#64748b' }}> • {m.label}</span> : null}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        style={styles.btn}
                        onClick={async () => {
                          try { await navigator.clipboard.writeText(String(m.to_number_e164 || '')); } catch {}
                        }}
                      >Copy</button>
                      <button
                        style={styles.btn}
                        onClick={async () => {
                          // quick reveal via clipboard of raw (admins are allowed to self-verify)
                          try { await navigator.clipboard.writeText(String(m.to_number_e164 || '')); } catch {}
                        }}
                        title="(Copy raw value)"
                      >Reveal</button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div style={styles.kpi}>
                  <div style={styles.kpiLabel}>Last inbound</div>
                  <div style={{ color: '#e2e8f0', fontWeight: 700 }}>{fmtTs(selected?.sms?.last_inbound_at)}</div>
                </div>
                <div style={styles.kpi}>
                  <div style={styles.kpiLabel}>Last outbound</div>
                  <div style={{ color: '#e2e8f0', fontWeight: 700 }}>{fmtTs(selected?.sms?.last_outbound_at)}</div>
                </div>
              </div>

              <div style={{ marginTop: 14, color: '#93c5fd', fontWeight: 800 }}>Email</div>
              <div style={{ padding: '8px 0', borderBottom: '1px solid #1f2a44' }}>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>From</div>
                <div style={{ color: '#e2e8f0', fontSize: 14 }}>{data?.email?.from_email || '—'}</div>
              </div>
              {(data?.email?.dns || []).map(d => (
                <div key={d.domain} style={{ padding: '10px 0', borderBottom: '1px solid #1f2a44' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ color: '#e2e8f0', fontWeight: 700 }}>{d.domain}</div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>checked: {fmtTs(d.checked_at)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                    {statusPill(d.spf_ok === true, 'SPF', 'SPF')}
                    {statusPill(d.dkim_ok === true, 'DKIM', 'DKIM')}
                    {statusPill(d.dmarc_ok === true, 'DMARC', 'DMARC')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.card}>
          <div style={{ fontWeight: 800, color: '#e2e8f0' }}>Diagnostics block</div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>
            Paste into a support ticket / Slack for fast triage.
          </div>
          <pre style={styles.pre}>{diagnosticsBlock || '—'}</pre>
        </div>
      </div>
    </div>
  );
}

const styles = {
  loading: { color: '#e2e8f0', padding: 16 },
  card: {
    background: '#0b1220',
    border: '1px solid #1f2a44',
    borderRadius: 12,
    padding: 16,
  },
  title: { color: '#e2e8f0', fontSize: 18, fontWeight: 800 },
  btn: {
    background: '#334155',
    border: '1px solid #475569',
    color: '#e2e8f0',
    padding: '8px 10px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
  },
  kpi: {
    background: '#0f172a',
    border: '1px solid #1f2a44',
    borderRadius: 12,
    padding: 12,
  },
  kpiLabel: { color: '#64748b', fontSize: 12, marginBottom: 6 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 12, color: '#94a3b8', padding: '10px 8px', borderBottom: '1px solid #1f2a44' },
  td: { padding: '12px 8px', borderBottom: '1px solid #1f2a44', color: '#94a3b8', fontSize: 13, verticalAlign: 'top' },
  tr: {},
  grid2: { display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 16 },
  pre: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    background: '#0f172a',
    border: '1px solid #1f2a44',
    color: '#e2e8f0',
    overflowX: 'auto',
    fontSize: 12,
    lineHeight: 1.35,
  },
};

export default AdvancedComms;
